/** @jsx jsx */
import { useState, useContext, Fragment, useEffect } from 'react'
import { css, jsx } from '@emotion/core'
import { SearchBar } from '@livechat/design-system'

import { Context } from '../../context'
import api from '../../api'
import Survey from '../../components/Survey'
import Hint from '../../components/Hint'
import SurveyLabelsSelectFilter from '../../components/SurveyLabelsSelectFilter'
import { formatItem } from '../../components/SurveyLabels'
import { SurveysList, Templates, LoadingSurveysList } from '../../components'

import { checkLocalStorage } from '../../utils'

const showTemplates = false

const mainCss = css`
  width: calc(100vw - 210px);
  max-height: calc(100vh - 56px);
  padding-left: 10px;

  overflow-y: scroll;
  transition: all 0.2s ease-in-out;

  @media (max-width: 855px) {
    width: 100vw;
    height: calc(100vh - 50px);
    max-height: calc(100vh - 50px);
    padding-left: 0;
    justify-content: center;
  }

  .add-survey {
    width: 24px;
  }
`

const linkCss = css`
  color: #3f8cff;
  margin-top: 2px;
  cursor: pointer;

  :hover {
    text-decoration: underline;
  }
`

const emptyStateCss = css`
  width: 100%;
  height: calc(100vh - 56px);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  .labelContainer {
    padding-bottom: 150px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;

    .label-top {
      font-size: 20px;
      margin-bottom: 10px;
    }

    .label-bottom {
      font-weight: 300;
    }
  }
`

const Surveys = () => {
  const {
    surveys,
    surveysLoading,
    removeSurvey,
    switchSurveyStatus,
    surveyToken,
    isHintOpen,
    setIsHintOpen,
    redirectToSandbox,
  } = useContext(Context)
  const [removed, setRemoved] = useState([])
  const [shouldShowHint, setShouldShowHint] = useState(false)
  const [searchValue, setSearchValue] = useState('')

  const [licenseGroupingLabels, setLicenseGroupingLabels] = useState([])
  const [isFetching, setIsFetching] = useState(false)
  const [activeLabelsFilters, setActiveLabelsFilters] = useState([])

  useEffect(() => {
    const request = async () => {
      setIsFetching(true)
      const licenseSettings = await api.fetchSettings(surveyToken)
      const groupingLabels = licenseSettings
        ? JSON?.parse(licenseSettings)?.grouping_labels || []
        : []
      setLicenseGroupingLabels(groupingLabels)
      setIsFetching(false)
    }

    request()
  }, [])

  const onDropSurvey = async (id) => {
    const status = await api.deleteSurvey(id, surveyToken)

    if (status === 200) {
      setRemoved([...removed, id])
      setTimeout(() => removeSurvey(id), 150)
    }
  }

  const onSwitchSurveyStatus = async (id, active) => {
    switchSurveyStatus(id, !active)
    const status = active
      ? await api.deactivateSurvey(id, surveyToken)
      : await api.activateSurvey(id, surveyToken)

    if (status !== 'OK') {
      switchSurveyStatus(id, active)
    }
  }

  useEffect(() => {
    if (checkLocalStorage()) {
      const timer = setTimeout(() => {
        setShouldShowHint(!localStorage.getItem('dontShowHint'))
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [])

  const filteredSurveys = surveys.filter((el) => {
    const isMatchedByName = searchValue
      ? el.name?.toLowerCase()?.includes(searchValue?.toLowerCase())
      : true

    const surveyLabels = el?.grouping_labels || []

    const isMatchedByLabel =
      activeLabelsFilters.length > 0
        ? activeLabelsFilters.every((l) =>
            surveyLabels.includes(l?.props?.value)
          )
        : true

    return isMatchedByName && isMatchedByLabel
  })

  return (
    <div css={mainCss}>
      {surveys?.length > 0 && (
        <div
          css={css`
            width: 100%;
            display: flex;

            @media (max-width: 855px) {
              justify-content: center;
            }
          `}
        >
          <SearchBar
            placeholder="Search for a survey.."
            onChange={setSearchValue}
            value={searchValue}
            css={css`
              height: 40px;
              margin-top: 20px;
              margin-left: 10px;
              width: 504px;

              .lc-search-bar__container {
                height: 40px;
              }

              .lc-input {
                height: 40px;
              }
            `}
          />
          {licenseGroupingLabels.length > 0 && (
            <div
              css={css`
                margin-top: 20px;
                margin-left: 20px;
              `}
            >
              <SurveyLabelsSelectFilter
                licenseGroupingLabels={
                  licenseGroupingLabels?.map((l) => formatItem(l)) || []
                }
                selectedItems={activeLabelsFilters}
                setSelectedItems={setActiveLabelsFilters}
              />
            </div>
          )}
        </div>
      )}
      {surveysLoading ? (
        <LoadingSurveysList showTemplates={showTemplates} />
      ) : (
        <Fragment>
          {showTemplates && (
            <SurveysList
              label="Weekly Templates"
              info="Check out ready-to-use examples!"
            >
              <Templates />
            </SurveysList>
          )}

          <SurveysList>
            {filteredSurveys?.length > 0 ? (
              filteredSurveys.map((el, index) => {
                const { id, name, active, structure, count, publicToken } = el

                return (
                  <Survey
                    survey={el}
                    id={id}
                    key={`${id}-${index}`}
                    name={name}
                    count={count}
                    active={active}
                    structure={structure}
                    publicToken={publicToken}
                    handleActive={() => onSwitchSurveyStatus(id, active)}
                    isRemoved={removed.includes(id)}
                    handleDrop={() => onDropSurvey(id)}
                  />
                )
              })
            ) : (
              <div css={emptyStateCss}>
                <div className="labelContainer">
                  <span className="label-top">You don't have any surveys.</span>
                  <span className="label-bottom">
                    Please{' '}
                    <span css={linkCss} onClick={redirectToSandbox}>
                      create new one
                    </span>
                    !
                  </span>
                </div>
              </div>
            )}
          </SurveysList>
        </Fragment>
      )}
      {shouldShowHint && isHintOpen && <Hint setIsOpen={setIsHintOpen} />}
    </div>
  )
}

export default Surveys
